import { AnimatePresence } from 'framer-motion';
import { Bell, Check, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getApiRoot } from '../../lib/env';
import { getAuthBearerHeaders } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { TourStyleCard } from '../ui/TourStyleCard';
import type { ForecastLocationContext } from './useForecastAtPoint';
import { WEATHER_BTN_DISMISS, WEATHER_BTN_SUBSCRIBE } from './weatherDesign';

interface WeatherSubscribeModalProps {
  open: boolean;
  onClose: () => void;
  lat: number;
  lng: number;
  location: ForecastLocationContext;
  onSubscribed?: (email: string) => void;
}

type SubscribeOutcome = 'subscribed' | 'already_subscribed' | 'reactivated';

const CHECKBOX_CLASS =
  'mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-800';

/** Subscribe to forecast emails — product-tour card styling. */
export function WeatherSubscribeModal({
  open,
  onClose,
  lat,
  lng,
  location,
  onSubscribed,
}: WeatherSubscribeModalProps) {
  const userEmail = useAuthStore((s) => s.user?.email ?? '');
  const [email, setEmail] = useState('');
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [daily48h, setDaily48h] = useState(true);
  const [alertThresholds, setAlertThresholds] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<SubscribeOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOutcome(null);
    setError(null);
    setEmail(userEmail);
  }, [open, userEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const apiRoot = getApiRoot();
    const token = localStorage.getItem('auth_token');
    if (!apiRoot || !email.trim() || !token) {
      setError('Please sign in to subscribe to forecast alerts.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${apiRoot}/weather/forecast/subscribe`, {
        method: 'POST',
        headers: getAuthBearerHeaders(true),
        body: JSON.stringify({
          email: email.trim(),
          lat,
          lng,
          weekly_digest: weeklyDigest,
          daily_48h: daily48h,
          alert_thresholds: alertThresholds,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const detail = body?.detail;
        if (typeof detail === 'string' && detail.includes('weather_forecast_subscription')) {
          throw new Error(
            'Email alerts are not set up on this server yet. Please try again later.',
          );
        }
        throw new Error(
          typeof detail === 'string' ? detail : 'Could not subscribe. Please try again.',
        );
      }
      const data = (await res.json()) as { status?: string };
      const status = data.status ?? 'subscribed';
      const subscribedEmail = email.trim();
      onSubscribed?.(subscribedEmail);
      if (status === 'already_subscribed') {
        setOutcome('already_subscribed');
      } else if (status === 'reactivated') {
        setOutcome('reactivated');
      } else {
        setOutcome('subscribed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Subscription failed');
    } finally {
      setSubmitting(false);
    }
  };

  const areaLabel = location.district_name ?? 'your area';
  const locationLine = [location.district_name, location.postcode].filter(Boolean).join(' · ');

  const successTitle =
    outcome === 'already_subscribed'
      ? "You're already subscribed"
      : outcome === 'reactivated'
        ? 'Welcome back'
        : "You're subscribed";

  const successBody =
    outcome === 'already_subscribed'
      ? `This email is already signed up for daily weather updates for ${areaLabel}. You do not need to subscribe again.`
      : "We'll send a daily email each morning around 7:00 AM Ghana time with your 7-day outlook and the next 48 hours. You'll also get alerts when rain, wind, or heat crosses your chosen limits.";

  return (
    <AnimatePresence>
      {open && (
        <div
          className="pointer-events-auto fixed inset-0 z-[65] flex items-center justify-center bg-black/40 p-4 dark:bg-black/55"
          role="presentation"
          onClick={onClose}
        >
          <div
            className="w-full max-w-md"
            role="dialog"
            aria-modal="true"
            aria-labelledby="weather-subscribe-title"
            onClick={(e) => e.stopPropagation()}
          >
            <TourStyleCard
              embedded
              headerIcon={<Bell size={16} className="text-white shrink-0" />}
              headerLabel="Stay informed"
              title="Daily weather by email"
              onDismiss={onClose}
              dismissLabel="Close subscription form"
              maxWidthClassName="max-w-md"
              footer={
                outcome ? (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full py-2.5 text-sm font-medium bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-xl hover:opacity-90 transition-opacity shadow-sm"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:gap-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className={`${WEATHER_BTN_DISMISS} sm:flex-1`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      form="weather-subscribe-form"
                      disabled={submitting || !email.trim()}
                      className={`${WEATHER_BTN_SUBSCRIBE} sm:flex-1 disabled:opacity-50`}
                    >
                      <Bell size={16} aria-hidden />
                      {submitting ? 'Subscribing…' : 'Subscribe for daily forecast'}
                    </button>
                  </div>
                )
              }
            >
              {locationLine && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{locationLine}</p>
              )}

              {outcome ? (
                <div className="py-2 text-center">
                  <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/40 ring-1 ring-emerald-200/80 dark:ring-emerald-800/50">
                    <Check size={20} className="text-emerald-600 dark:text-emerald-400" aria-hidden />
                  </div>
                  <p id="weather-subscribe-title" className="text-sm font-semibold text-gray-900 dark:text-white">
                    {successTitle}
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{successBody}</p>
                </div>
              ) : (
                <form id="weather-subscribe-form" onSubmit={handleSubmit} className="space-y-3">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Email address</span>
                    <div className="relative mt-1.5">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-emerald-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
                      />
                    </div>
                  </label>

                  <fieldset className="space-y-2 rounded-xl border border-gray-200/80 bg-gray-50/80 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800/50">
                    <legend className="px-1 text-xs font-medium text-gray-700 dark:text-gray-300">Send me</legend>
                    <label className="flex items-start gap-2.5 text-xs leading-snug text-gray-600 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={weeklyDigest}
                        onChange={(e) => setWeeklyDigest(e.target.checked)}
                        className={CHECKBOX_CLASS}
                      />
                      7-day outlook, sent daily each morning around 7:00 AM Ghana time
                    </label>
                    <label className="flex items-start gap-2.5 text-xs leading-snug text-gray-600 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={daily48h}
                        onChange={(e) => setDaily48h(e.target.checked)}
                        className={CHECKBOX_CLASS}
                      />
                      Next 48 hours included in the same daily email
                    </label>
                    <label className="flex items-start gap-2.5 text-xs leading-snug text-gray-600 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={alertThresholds}
                        onChange={(e) => setAlertThresholds(e.target.checked)}
                        className={CHECKBOX_CLASS}
                      />
                      Alerts when rain, wind, or heat goes above your limits
                    </label>
                  </fieldset>

                  {error && (
                    <div
                      className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-xs text-red-600 dark:text-red-400"
                      role="alert"
                    >
                      {error}
                    </div>
                  )}
                </form>
              )}
            </TourStyleCard>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
